import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';
import { hasEmoji } from '@/shared/utils/emoji';

import {
  useDiseaseCodesQuery,
  useDiseaseGroupsQuery,
} from '../hooks/use-diagnosis-options';
import { useUpdatePatient } from '../hooks/use-patient-mutations';
import { usePatientQuery } from '../hooks/use-patients';
import { usePatientDetailStore } from '../store/patient-detail.store';
import {
  PatientApiError,
  type Patient,
  type PatientApiErrorCode,
  type PatientGender,
  type PatientGuardianRelationship,
  type PatientMedication,
  type UpdatePatientInput,
} from '../types/patient.types';
import {
  dashIfBlank,
  formatDate,
  formatDateTime,
  formatUid,
  maskName,
  maskPhone,
} from '../utils/format-patient';
import {
  GUARDIAN_NAME_MAX,
  GUARDIAN_NAME_MIN,
  MEDICATION_MAX,
  PATIENT_NAME_MAX,
  PATIENT_NAME_REGEX,
  RESIDENCE_DETAIL_MAX,
  RESIDENCE_MAX,
  initialMedicationRows,
  medicationRows,
  toDateInputValue,
  todayInKst,
} from '../utils/patient-field-rules';
import {
  PHONE_DIGITS_MAX,
  PHONE_REGEX,
  toPhoneInputValue,
} from '@/shared/utils/phone';
import { PatientStatusBadge } from './PatientStatusBadge';

// PRD 02 §3.6: 480 px wide, opens on row click.
//
// SHCT-737 — the panel is grouped into the FOUR sections §3.6 defines (기본 정보 / 진료 정보 /
// 앱·계정 / 이력). Before this the fields were listed flat, with no relation to what they meant, so
// 퇴원일 and 회원가입일 landed diagonally opposite each other and were never read together.
//
// SHCT-737 v1.17 — 키·체중 are gone. The values stopped being collected in 2026-06 (the patient app
// dropped them at signup and the third lifelog metric became 안정시 심박수), so the panel was showing
// two fields nothing can ever fill.
//
// SHCT-737 v1.16 §3.6 「편집 가능 필드」 — the panel now CORRECTS, reversing the older 읽기 전용 rule.
// The shape is the one §3.6 동작 시나리오 3 names: 값을 클릭해 그 자리에서 고치고, 저장·취소는
// 필드 단위로 처리한다. A whole-panel edit mode would have to replay every field on save and could
// overwrite a value someone else corrected in the meantime; a per-field save sends only what changed,
// which is also exactly what the backend contract reads (an absent key = leave alone).
const PANEL_WIDTH = 480;

// Which field is open for editing. `diagnosis` covers 주진단 + 부진단 together and `medication` covers
// 약품명 + 복약주기 together — see the editors below for why neither can be split.
type EditKey =
  | 'name'
  | 'gender'
  | 'birthDate'
  | 'phone'
  | 'residence'
  | 'residenceDetail'
  | 'diagnosis'
  | 'medication'
  | 'dischargeDate'
  | 'guardianName'
  | 'guardianRelationship';

const GUARDIAN_RELATIONSHIPS: readonly PatientGuardianRelationship[] = [
  'SPOUSE',
  'CHILD',
  'PARENT',
  'SIBLING',
  'OTHER',
];

const GENDERS: readonly PatientGender[] = ['F', 'M'];

// One §3.6 section: a heading plus its own field grid, so the grouping is visible rather than implied
// by field order alone.
function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--space-5)' }}>
      <h3
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--muted-foreground)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          margin: '0 0 var(--space-2)',
        }}
      >
        {title}
      </h3>
      {/* The 2-column grid is set here, not left to `.detail-grid`: globals.css redefines that class
          further down as `140px 1fr` for `AdminPrototypePage`'s label/value layout, and that late rule
          wins. Fixing the global would move a screen this ticket does not touch. */}
      <div
        className="detail-grid"
        style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}
      >
        {children}
      </div>
    </section>
  );
}

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  // Span both grid columns — for a value that is a list rather than a single line.
  span?: boolean;
}

function DetailField({ label, value, span }: DetailFieldProps) {
  return (
    <div
      className="detail-field"
      // SHCT-738 — PRD 02 v1.20 §3.6 puts 처방약 on a full-width row: it is a multi-entry list, and a
      // list inside one cell of the 2-column grid makes the neighbouring row's height jump with it.
      // Set here rather than as a new `globals.css` class — that file is shared by every screen and the
      // app is live on UAT, so a one-field layout need does not earn a global rule.
      style={span ? { gridColumn: '1 / -1' } : undefined}
    >
      <span className="detail-field__label">{label}</span>
      <span className="detail-field__value">{value}</span>
    </div>
  );
}

/**
 * A field §3.6 marks 수정 불가. The value is NOT a button and clicking it does nothing — §3.6 조건 분기
 * requires that a locked field never enters edit mode and explains itself by tooltip instead. Every
 * lock here has a stated reason; a field is never locked merely because it was not built.
 *
 * <p>The reason is carried by the tooltip ALONE. An earlier build also drew a 🔒 next to the value,
 * taken from the ticket's "회색 처리 + 잠금 표시" wording — but neither PRD 02 v1.20 §3.6 (which
 * specifies only 툴팁으로 사유 안내) nor the prototype has that glyph, and it made every read-only row
 * shout on a panel where most rows are read-only.
 */
function LockedField({
  label,
  value,
  reason,
  span,
}: {
  label: string;
  value: React.ReactNode;
  reason: string;
  span?: boolean;
}) {
  return (
    <DetailField
      label={label}
      span={span}
      value={
        <span title={reason} aria-label={reason} style={{ cursor: 'help' }}>
          {value}
        </span>
      }
    />
  );
}

/**
 * Shell shared by every inline editor: the editor's own controls, its validation message, and the
 * interaction that commits them. Holds no draft of its own — each editor owns its draft so cancelling
 * one field cannot disturb another.
 *
 * <p><b>Leaving the field saves it.</b> Click out or press Enter to commit, Escape to abandon the
 * change — the admin prototype's `patients.html` behaviour, and the reason there are no 저장 / 취소
 * buttons. An earlier build put that pair under every open field; it doubled the height of a 480px
 * panel row and made a one-word correction a three-click errand. PRD 02 v1.20 §3.6 asks only that
 * 저장·취소 be handled PER FIELD rather than for the whole form, which this satisfies — the unit of
 * commit is still the single field.
 *
 * <p>A commit refused by validation keeps the editor open with its message, so the invalid value is
 * never silently dropped and never silently saved. `saving` suppresses both gestures while the
 * mutation is in flight, so a stray click cannot fire a second write.
 */
function InlineEditShell({
  children,
  error,
  saving,
  onSave,
  onCancel,
}: {
  children: React.ReactNode;
  error: string | null;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('admin');

  // Leaving the field commits it. `relatedTarget` is what focus moved TO, so a move BETWEEN this
  // editor's own controls (주진단 → 부진단, 약품명 → 복약주기) is not a departure and must not save a
  // half-made change. It is null when focus lands on something unfocusable — clicking the panel
  // background — which is the ordinary "click out" and does commit.
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (saving) return;
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    onSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (saving) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {children}
      {error && (
        <span role="alert" style={{ color: 'var(--destructive)', fontSize: 'var(--font-size-xs)' }}>
          {error}
        </span>
      )}
      {saving && (
        <span style={{ color: 'var(--muted-foreground)', fontSize: 'var(--font-size-xs)' }}>
          {t('patientManagement.actions.saving')}
        </span>
      )}
    </div>
  );
}

/**
 * An editable field's read state: the value plus the affordance that it can be corrected. When the Role
 * lacks `PATIENT:WRITE` this renders the §7.4 P-2 pair — disabled control + the standard tooltip — rather
 * than hiding the affordance, because an operator who cannot see the control cannot learn that a
 * permission is what stops them.
 */
function EditableValue({
  value,
  canWrite,
  denyTitle,
  onStart,
  editLabel,
  editHint,
}: {
  value: React.ReactNode;
  canWrite: boolean;
  denyTitle?: string;
  onStart: () => void;
  editLabel: string;
  editHint: string;
}) {
  return (
    <button
      type="button"
      className="detail-field__edit-trigger"
      onClick={onStart}
      disabled={!canWrite}
      aria-label={editLabel}
      // The P-2 deny copy has to win when the Role may not write (PRD 21 §7.4), so both tooltips are
      // decided in ONE attribute rather than by a spread a later `title` could overwrite.
      title={canWrite ? editHint : denyTitle}
      {...(canWrite ? {} : { 'data-perm-denied': 'true' })}
      style={{
        background: 'none',
        border: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        cursor: canWrite ? 'pointer' : 'not-allowed',
      }}
    >
      {value}
      {/* The prototype's edit affordance: a faint pencil after the value. It replaces a dotted
          underline, which read as a spelling error on a panel of Korean names and ICD codes. */}
      {canWrite && (
        <Pencil
          aria-hidden
          style={{
            width: 11,
            height: 11,
            marginLeft: 5,
            opacity: 0.45,
            verticalAlign: -1,
            display: 'inline-block',
          }}
        />
      )}
    </button>
  );
}

// The editing controls are the design system's own `form-field__input` / `form-field__select`, shrunk
// the way the prototype shrinks them (28px tall, 13px text) so an open field does not grow the row it
// sits in. Only the compacting is inline; the border, focus ring, background and select chevron come
// from the shared classes, which is why an earlier hand-rolled box looked subtly foreign here.
const inputStyle: React.CSSProperties = {
  height: 28,
  fontSize: 13,
  padding: '0 6px',
};

// Same, plus room for the chevron the select class paints on the right.
const selectStyle: React.CSSProperties = {
  height: 28,
  fontSize: 13,
  padding: '0 26px 0 6px',
  backgroundPosition: 'right 6px center',
  backgroundSize: '16px 16px',
};

// ── Editors ────────────────────────────────────────────────────────────────
// Each owns its draft, validates against [공통] 00 입력검증표준, and hands the parent the PARTIAL payload
// for just its own field(s).

function TextEditor({
  initial,
  maxLength,
  placeholder,
  sanitize,
  validate,
  serverError,
  saving,
  buildInput,
  onSave,
  onCancel,
}: {
  initial: string;
  maxLength: number;
  placeholder?: string;
  /** Narrows what the field can hold WHILE typing (휴대폰: digits only) — never a silent truncation. */
  sanitize?: (raw: string) => string;
  /** The field's own rule, checked after the shared length/이모지 checks. Returns an error message. */
  validate?: (value: string) => string | null;
  /**
   * A rejection the SERVER raised for this field — a number another patient already holds, say. Shown in
   * the same place the local rules report, because it is the same kind of answer: this value cannot be
   * saved, here is why, the editor stays open so it can be corrected.
   */
  serverError?: string | null;
  saving: boolean;
  buildInput: (value: string) => UpdatePatientInput;
  onSave: (input: UpdatePatientInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('admin');
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = value.trim();
    // Nothing was typed, so there is nothing to save (SHCT-737 QC). 이름·보호자 이름·환자 휴대폰 open
    // EMPTY because the detail response masks them, so simply clicking out of one used to submit '' and
    // report a validation error for a change the operator never made. Closing without a write is also
    // the honest answer for an untouched field generally: the commit gesture here is 「leaving the
    // field」, which someone performs by accident far more often than they perform a save.
    if (trimmed === initial.trim()) {
      onCancel();
      return;
    }
    if (trimmed.length > maxLength) {
      setError(t('patientManagement.panel.edit.errors.tooLong', { max: maxLength }));
      return;
    }
    if (hasEmoji(trimmed) || /[\r\n]/.test(trimmed)) {
      setError(t('patientManagement.panel.edit.errors.invalidChars'));
      return;
    }
    const failed = validate?.(trimmed);
    if (failed) {
      setError(failed);
      return;
    }
    onSave(buildInput(trimmed));
  };

  return (
    <InlineEditShell
      error={error ?? serverError ?? null}
      saving={saving}
      onSave={submit}
      onCancel={onCancel}
    >
      <input
        autoFocus
        type="text"
        value={value}
        placeholder={placeholder}
        // No `maxLength` here: nothing strips characters on change, so the browser cap and the checked
        // rule agree — but the message still has to be reachable when a paste exceeds it.
        onChange={(e) => {
          setValue(sanitize ? sanitize(e.target.value) : e.target.value);
          setError(null);
        }}
        className="form-field__input"
        style={inputStyle}
      />
    </InlineEditShell>
  );
}

function SelectEditor<T extends string>({
  initial,
  options,
  optionLabel,
  placeholder,
  saving,
  buildInput,
  onSave,
  onCancel,
}: {
  initial: T | '';
  options: readonly T[];
  optionLabel: (value: T) => string;
  placeholder: string;
  saving: boolean;
  buildInput: (value: T) => UpdatePatientInput;
  onSave: (input: UpdatePatientInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('admin');
  const [value, setValue] = useState<T | ''>(initial);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    // Unchanged — close without a write (see TextEditor.submit).
    if (value === initial) {
      onCancel();
      return;
    }
    if (!value) {
      setError(t('patientManagement.panel.edit.errors.required'));
      return;
    }
    onSave(buildInput(value));
  };

  return (
    <InlineEditShell error={error} saving={saving} onSave={submit} onCancel={onCancel}>
      <select
        autoFocus
        value={value}
        onChange={(e) => {
          setValue(e.target.value as T);
          setError(null);
        }}
        className="form-field__select"
        style={selectStyle}
      >
        {/* SHCT-737 QC — a required field offers no way back to 「선택」. The option is kept but
            DISABLED rather than deleted: a record whose value is missing still has to render as the
            placeholder, and dropping it would silently show the first real option while the stored
            value is empty. Disabled means it displays but cannot be chosen. 부진단 is the one select
            that keeps this option live, because there it is how the value is removed. */}
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {optionLabel(o)}
          </option>
        ))}
      </select>
    </InlineEditShell>
  );
}

function DateEditor({
  initial,
  max,
  saving,
  buildInput,
  onSave,
  onCancel,
  requireBeforeToday,
}: {
  initial: string;
  max?: string;
  saving: boolean;
  buildInput: (value: string) => UpdatePatientInput;
  onSave: (input: UpdatePatientInput) => void;
  onCancel: () => void;
  requireBeforeToday?: boolean;
}) {
  const { t } = useTranslation('admin');
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    // Unchanged — close without a write (see TextEditor.submit). This also keeps the 퇴원일 「PRO 스케줄
    // 재계산」 confirm from appearing for a date nobody edited.
    if (value === initial) {
      onCancel();
      return;
    }
    if (!value) {
      setError(t('patientManagement.panel.edit.errors.required'));
      return;
    }
    // §4.2 — 퇴원일은 오늘 이전 날짜만. 생년월일도 BE 가 미래를 거부하므로(PATIENT_DOB_FUTURE_INVALID)
    // 같은 경계를 화면에서 먼저 잡아 저장 실패로 배우지 않게 한다.
    if (requireBeforeToday && value > todayInKst()) {
      setError(t('patientManagement.panel.edit.errors.notFuture'));
      return;
    }
    onSave(buildInput(value));
  };

  return (
    <InlineEditShell error={error} saving={saving} onSave={submit} onCancel={onCancel}>
      <input
        autoFocus
        type="date"
        value={value}
        max={max}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        className="form-field__input"
        style={inputStyle}
      />
    </InlineEditShell>
  );
}

/**
 * 주진단 + 부진단 in ONE editor, and they cannot be split.
 *
 * The backend validates the PAIR: it resolves the resulting group and the resulting code (each falling
 * back to the stored value) and rejects the save when the code does not belong to the group. Editing
 * 주진단 alone would therefore fail whenever the existing 부진단 belongs to the old group — a save that
 * is refused for a reason the operator cannot see or fix from that field.
 *
 * The detail response carries diagnosis by NAME only, with no ids, so the current selection is resolved
 * back to ids by matching the master lists: the group by its name (five clinical domains, unique) and
 * the code by its ICD code (unique within a group). A name that does not resolve simply starts the
 * editor unselected rather than guessing.
 */
function DiagnosisEditor({
  patient,
  saving,
  onSave,
  onCancel,
}: {
  patient: Patient;
  saving: boolean;
  onSave: (input: UpdatePatientInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('admin');
  const groupsQuery = useDiseaseGroupsQuery();
  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

  const initialGroupId = useMemo(() => {
    const match = groups.find((g) => g.name === patient.mainDiagnosisName);
    return match?.id ?? null;
  }, [groups, patient.mainDiagnosisName]);

  const [groupId, setGroupId] = useState<number | null>(null);
  const [codeId, setCodeId] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveGroupId = touched ? groupId : initialGroupId;
  const codesQuery = useDiseaseCodesQuery(effectiveGroupId);
  const codes = useMemo(() => codesQuery.data ?? [], [codesQuery.data]);

  const initialCodeId = useMemo(() => {
    if (!patient.diseaseCode) return null;
    return codes.find((c) => c.code === patient.diseaseCode)?.id ?? null;
  }, [codes, patient.diseaseCode]);
  const effectiveCodeId = touched ? codeId : initialCodeId;

  const submit = () => {
    // Unchanged — close without a write (see TextEditor.submit). Compared by id rather than by the
    // `touched` flag, so re-picking the value that was already selected is not treated as an edit.
    if (effectiveGroupId === initialGroupId && effectiveCodeId === initialCodeId) {
      onCancel();
      return;
    }
    if (effectiveGroupId == null) {
      setError(t('patientManagement.panel.edit.errors.required'));
      return;
    }
    // SHCT-737 QC ② — 부진단 is optional (PRD 02 §3.7), so removing it is a THIRD instruction, not a
    // variant of setting it: an omitted field and a null field reach the server identically, so the code
    // field alone can say 「set it」 and 「leave it」 but not 「remove it」. That is why picking the blank
    // option used to report a successful save and change nothing. The removal travels on its own flag,
    // and the server ignores the code when it is set.
    onSave(
      effectiveCodeId == null
        ? { mainDiagnosisGroupId: effectiveGroupId, clearSubDiagnosis: true }
        : { mainDiagnosisGroupId: effectiveGroupId, subDiagnosisCodeId: effectiveCodeId },
    );
  };

  return (
    <InlineEditShell error={error} saving={saving} onSave={submit} onCancel={onCancel}>
      <select
        autoFocus
        value={effectiveGroupId ?? ''}
        onChange={(e) => {
          const next = e.target.value ? Number(e.target.value) : null;
          setTouched(true);
          setGroupId(next);
          // A code belongs to exactly one group, so changing the group invalidates the current code.
          setCodeId(null);
          setError(null);
        }}
        aria-label={t('patientManagement.panel.fields.mainDiagnosis')}
        className="form-field__select"
        style={selectStyle}
      >
        {/* 주진단 is required — placeholder disabled, same rule as SelectEditor above. */}
        <option value="" disabled>
          {t('patientManagement.registrationModal.patient.primaryDiagnosisPlaceholder')}
        </option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
      <select
        value={effectiveCodeId ?? ''}
        disabled={effectiveGroupId == null}
        onChange={(e) => {
          setTouched(true);
          setGroupId(effectiveGroupId);
          setCodeId(e.target.value ? Number(e.target.value) : null);
          setError(null);
        }}
        aria-label={t('patientManagement.panel.fields.subDiagnosis')}
        className="form-field__select"
        style={selectStyle}
      >
        {/* 부진단 is OPTIONAL (PRD 02 §3.7), so this option stays selectable — picking it is how the
             부진단 is removed, which is the QC ② fix that travels as `clearSubDiagnosis`. */}
        <option value="">
          {t('patientManagement.registrationModal.patient.secondaryDiagnosisPlaceholder')}
        </option>
        {codes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.code} {c.name}
          </option>
        ))}
      </select>
    </InlineEditShell>
  );
}

/**
 * 처방약 — a LIST of 약품명 + 복약주기 pairs, edited as one field (SHCT-737 QC ④ · SHCT-738).
 *
 * PRD 02 v1.20 §3.6 and the registration form both treat 처방약 as multi-entry, and `pt_patient_medication`
 * (V96) stores it that way. The panel therefore edits rows, not one pair — but only when the server proved
 * it reads rows, by sending `medications` on the detail response. Against a server that still stores the
 * superseded single pair, a multi-row form would let an operator add a second drug, report a successful
 * save and lose it, which is worse than the missing feature. So the row controls appear with the field.
 *
 * The pairing is why 약품명 and 복약주기 cannot be separate editors: "metformin / twice a day" only means
 * anything as a pair, and the server replaces the whole list on save.
 */
function MedicationEditor({
  patient,
  saving,
  onSave,
  onCancel,
}: {
  patient: Patient;
  saving: boolean;
  onSave: (input: UpdatePatientInput) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('admin');
  const supportsList = Array.isArray(patient.medications);
  const [rows, setRows] = useState<PatientMedication[]>(() =>
    initialMedicationRows(patient),
  );
  // Frozen at mount, alongside the draft above, so a background refetch of `patient` cannot move the
  // baseline the 「unchanged」 check compares against mid-edit.
  const [initialRows] = useState<PatientMedication[]>(() => initialMedicationRows(patient));
  const [error, setError] = useState<string | null>(null);

  const setRow = (index: number, patch: Partial<PatientMedication>) => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
    setError(null);
  };

  const submit = () => {
    const trimmed = rows.map((r) => ({ name: r.name.trim(), freq: r.freq.trim() }));
    // Unchanged — close without a write (see TextEditor.submit). Covers the patient with no
    // prescription at all, whose editor opens on one blank row: leaving it used to save an empty list.
    const untouched =
      trimmed.length === initialRows.length &&
      trimmed.every(
        (r, i) =>
          r.name === initialRows[i].name.trim() && r.freq === initialRows[i].freq.trim(),
      );
    if (untouched) {
      onCancel();
      return;
    }
    if (trimmed.some((r) => r.name.length > MEDICATION_MAX || r.freq.length > MEDICATION_MAX)) {
      setError(t('patientManagement.panel.edit.errors.tooLong', { max: MEDICATION_MAX }));
      return;
    }
    if (
      trimmed.some(
        (r) =>
          hasEmoji(r.name) ||
          hasEmoji(r.freq) ||
          /[\r\n]/.test(r.name) ||
          /[\r\n]/.test(r.freq),
      )
    ) {
      setError(t('patientManagement.panel.edit.errors.invalidChars'));
      return;
    }
    // A 복약주기 with no 약품명 has nothing to attach to — PRD 02 §3.7 makes the drug the required half
    // of the pair — and an operator who emptied a row meant to delete it.
    if (trimmed.some((r) => !r.name && r.freq)) {
      setError(t('patientManagement.panel.edit.errors.medicationNameRequired'));
      return;
    }
    const kept = trimmed.filter((r) => r.name);
    if (!supportsList) {
      // Legacy wire: one pair is all the server can store, so send exactly that.
      const only = kept[0] ?? { name: '', freq: '' };
      onSave({ prescriptionDrug: only.name, medicationCycle: only.freq });
      return;
    }
    onSave({ medications: kept });
  };

  return (
    <InlineEditShell error={error} saving={saving} onSave={submit} onCancel={onCancel}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            autoFocus={i === 0}
            type="text"
            value={row.name}
            placeholder={t('patientManagement.registrationModal.patient.medicationPlaceholder')}
            aria-label={t('patientManagement.registrationModal.patient.medication')}
            onChange={(e) => setRow(i, { name: e.target.value })}
            className="form-field__input"
            style={inputStyle}
          />
          <input
            type="text"
            value={row.freq}
            placeholder={t(
              'patientManagement.registrationModal.patient.medicationFrequencyPlaceholder',
            )}
            aria-label={t('patientManagement.registrationModal.patient.medicationFrequency')}
            onChange={(e) => setRow(i, { freq: e.target.value })}
            className="form-field__input"
            style={inputStyle}
          />
          {supportsList && rows.length > 1 && (
            <button
              type="button"
              className="btn btn--ghost"
              aria-label={t('patientManagement.panel.edit.medication.remove')}
              title={t('patientManagement.panel.edit.medication.remove')}
              onClick={() => {
                setRows((current) => current.filter((_, at) => at !== i));
                setError(null);
              }}
            >
              −
            </button>
          )}
        </div>
      ))}
      {supportsList && (
        <button
          type="button"
          className="btn btn--ghost"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setRows((current) => [...current, { name: '', freq: '' }]);
            setError(null);
          }}
        >
          + {t('patientManagement.panel.edit.medication.add')}
        </button>
      )}
    </InlineEditShell>
  );
}

// ── Field grid ─────────────────────────────────────────────────────────────

interface FieldGridProps {
  patient: Patient;
  canWrite: boolean;
  denyTitle?: string;
  editing: EditKey | null;
  onStart: (key: EditKey) => void;
  onCancel: () => void;
  onSave: (input: UpdatePatientInput, key: EditKey) => void;
  saving: boolean;
  /** A field-level rejection from the last save, shown by the editor that produced it. */
  serverError?: { key: EditKey; message: string } | null;
}

// PRD 02 v1.20 §3.6 「섹션 구조」 — 기본 정보 / 진료 정보 / 앱·계정 / 이력.
//
// Only the fields the wire can actually supply are rendered. A row the server never sends is left out
// rather than stubbed with a permanent "-": a dash means "미입력" in this panel (§3.6), so using it for
// "the server does not send this" would tell the operator the patient has no 처방약 when nobody knows.
//
// Exported for the render tests: the guardian rows appear or vanish on a rule ([ADM.063 ②]) rather than
// on data, the edit affordances turn on a permission, and the panel itself is only reachable through a
// query + a store.
export function PatientFieldGrid({
  patient,
  canWrite,
  denyTitle,
  editing,
  onStart,
  onCancel,
  onSave,
  saving,
  serverError,
}: FieldGridProps) {
  const { t } = useTranslation('admin');
  /** The message for one field, or null — an error belongs to the editor that caused it, not to all of them. */
  const errorFor = (key: EditKey) =>
    serverError?.key === key ? serverError.message : null;

  const diseaseText = patient.diseaseCode
    ? patient.diseaseName
      ? `${patient.diseaseCode} ${patient.diseaseName}`
      : patient.diseaseCode
    : '-';
  // [ADM.063 ①] — form ⓐ, 항목명 + 일시.
  const lastProText = patient.lastPro
    ? `${patient.lastPro.itemName} · ${formatDateTime(patient.lastPro.submittedAt)}`
    : '-';
  // SHCT-795 / SHCT-797 — PRD §3.6 gives every optional panel field the same empty state, "미입력 시 `-`",
  // and `?? '-'` only delivers it for null. The wire also carries "" (거주지 for a patient who never picked a
  // 구·동 in the app) and, until the backend has a column for it, nothing at all for 치료기관 — both of which
  // slipped through as a blank cell that reads as a broken panel rather than as "no value".
  const residenceText = dashIfBlank(patient.residence);
  // SHCT-915 — 상세 주소 is its own row with its own empty state. It is null far more often than 거주지 is
  // (the patient may skip it entirely), so it gets the same `-` rather than an empty cell.
  const residenceDetailText = dashIfBlank(patient.residenceDetail);
  const institutionText = dashIfBlank(patient.institution);
  // SHCT-738 [ADM.064 ②] — 주진단 shows the clinical domain name ALONE (e.g. `정신건강`). The ICD-10
  // chapter and range are deliberately not appended: at 480px the combined form wraps around 50
  // characters, which is why the customer chose the group name on its own.
  const mainDiagnosisText = dashIfBlank(patient.mainDiagnosisName);
  // SHCT-738 [ADM.064 ③] — 처방약 is a list of `약품명 · 복약주기`, kept in the order it was entered.
  // `medicationRows` reads the server's list when it sends one and falls back to the superseded single
  // `pt_patient` pair otherwise, so the panel shows every drug the day admin-api serves the table and
  // needs no change here when it does.
  const medications = medicationRows(patient).map((m) =>
    [m.name, m.freq].filter(Boolean).join(' · '),
  );
  // SHCT-794 — undefined (not a dash) while the wire omits the field; see the section comment below.
  const appUserTypeText = patient.appUserType
    ? t(`patientManagement.panel.appUserTypes.${patient.appUserType}`)
    : null;
  const guardianMode = patient.appUserType === 'guardian';

  const f = (key: string) => t(`patientManagement.panel.fields.${key}`);
  const s = (key: string) => t(`patientManagement.panel.sections.${key}`);
  const lock = (key: string) => t(`patientManagement.panel.locks.${key}`);

  // An editable row: the editor while this field is open, otherwise the value with its edit affordance.
  const editable = (key: EditKey, value: React.ReactNode, editor: React.ReactNode) =>
    editing === key ? (
      editor
    ) : (
      <EditableValue
        value={value}
        canWrite={canWrite}
        denyTitle={denyTitle}
        onStart={() => onStart(key)}
        editLabel={t('patientManagement.panel.edit.startAria', { field: f(key) })}
        editHint={t('patientManagement.panel.edit.hint')}
      />
    );

  return (
    <>
      <DetailSection title={s('basic')}>
        <LockedField label={f('pid')} value={formatUid(patient.id)} reason={lock('uid')} />
        {/* SHCT-737 — PRD 02 v1.20 §3.6 「패널 표시 필드」 이름 행 · §4.1 배지 2종.
            어드민은 목록·패널 어디에도 보호자 대리 여부 표시가 없었다(의료진웹은 환자 카드에 이미
            달고 있다 — 03 §2.3). 전체 환자를 다루는 운영 화면에서 대리 여부를 모르면 연락·안내 대상을
            틀리므로 표시 보강이 아니라 운영 안전 장치에 가깝다.
            탈퇴 배지는 여기 붙이지 않았다 — 그리드 위 상태 칩이 이미 `탈퇴` 를 보여주고 있어 한 패널
            안에서 같은 사실을 두 번 말하게 된다. 목록 이름 컬럼 쪽 배지는 목록 응답에
            `appUserType` 이 없어 아직 붙일 수 없다(BE 노트).
            §3.6 「수정 가능 필드」 — 이름. 상세 응답의 이름은 서버에서 마스킹된 값이므로(`PhiMasking.
            maskName`) 편집기를 그 값으로 채울 수 없다. 그래서 채우지 않는다: 이름 정정은 올바른 이름을
            새로 적는 일이라 빈 칸에서 시작하는 편이 맞고, 마스킹된 현재 값은 위에 그대로 남아 있다.
            SHCT-737 QC ③. */}
        <DetailField
          label={f('name')}
          value={editable(
            'name',
            maskName(patient.name),
            <TextEditor
              initial=""
              maxLength={PATIENT_NAME_MAX}
              placeholder={t('patientManagement.panel.edit.phiPlaceholder')}
              validate={(v) =>
                PATIENT_NAME_REGEX.test(v)
                  ? null
                  : t('patientManagement.panel.edit.errors.patientName')
              }
              serverError={errorFor('name')}
              saving={saving}
              buildInput={(name) => ({ name })}
              onSave={(input) => onSave(input, 'name')}
              onCancel={onCancel}
            />,
          )}
        />
        {/* §3.6 「본인 모드」 — 생년월일·성별은 앱 본인인증에서 받은 값이라 어드민이 고칠 수 없다.
            보호자 대리 모드에서는 의료진이 손으로 입력한 값이므로 오기 정정 대상이다. 서버도 같은
            규칙을 강제한다(PATIENT_IDENTITY_FIELD_LOCKED). */}
        {guardianMode ? (
          <DetailField
            label={f('gender')}
            value={editable(
              'gender',
              patient.gender ? t(`patientManagement.genders.${patient.gender}`) : '-',
              <SelectEditor<PatientGender>
                initial={patient.gender ?? ''}
                options={GENDERS}
                optionLabel={(g) => t(`patientManagement.genders.${g}`)}
                placeholder={t('patientManagement.registrationModal.patient.genderPlaceholder')}
                saving={saving}
                buildInput={(gender) => ({ gender })}
                onSave={(input) => onSave(input, 'gender')}
                onCancel={onCancel}
              />,
            )}
          />
        ) : (
          <LockedField
            label={f('gender')}
            value={patient.gender ? t(`patientManagement.genders.${patient.gender}`) : '-'}
            reason={lock('identitySelf')}
          />
        )}
        {guardianMode ? (
          <DetailField
            label={f('birthDate')}
            value={editable(
              'birthDate',
              formatDate(patient.birthDate),
              <DateEditor
                initial={toDateInputValue(patient.birthDate)}
                max={todayInKst()}
                requireBeforeToday
                saving={saving}
                buildInput={(birthDate) => ({ birthDate })}
                onSave={(input) => onSave(input, 'birthDate')}
                onCancel={onCancel}
              />,
            )}
          />
        ) : (
          <LockedField
            label={f('birthDate')}
            value={formatDate(patient.birthDate)}
            reason={lock('identitySelf')}
          />
        )}
        {/* SHCT-794 (QC) — 보호자 대리 환자는 환자 본인 휴대폰이 없을 수 있다(§3.7 「환자 휴대폰은 선택
            입력」). 그런 환자에서 이 칸이 빈 채로 남아 패널이 깨진 것처럼 보였다. §3.6 표는 전화번호 행에
            `미입력 시 -` 를 적어 두지 않았으나(거주지·처방약 등에는 있다), 빈 칸보다 `-` 가 읽기 명확하다는
            QC 판단에 따라 다른 선택 필드와 같은 빈 상태를 쓴다. */}
        {/* SHCT-737 §3.6 필드별 수정 범위 — 본인 모드에서 환자 휴대폰은 앱 인증 계정 키라 어드민이
            바꾸면 계정이 바뀐다(잠금). 보호자 모드에서는 인증 주체가 보호자이고 이 번호는 「선택
            연락처」(§3.7)일 뿐이므로 정정 대상이다. 마스킹된 값은 편집기에 채울 수 없어 이름과 같은
            이유로 빈 칸에서 시작한다. */}
        {guardianMode ? (
          <DetailField
            label={f('phone')}
            value={editable(
              'phone',
              dashIfBlank(maskPhone(patient.phone)),
              <TextEditor
                initial=""
                maxLength={PHONE_DIGITS_MAX}
                placeholder={t('patientManagement.panel.edit.phonePlaceholder')}
                sanitize={toPhoneInputValue}
                validate={(v) =>
                  PHONE_REGEX.test(v)
                    ? null
                    : t('patientManagement.panel.edit.errors.phone')
                }
                serverError={errorFor('phone')}
                saving={saving}
                buildInput={(phone) => ({ phone })}
                onSave={(input) => onSave(input, 'phone')}
                onCancel={onCancel}
              />,
            )}
          />
        ) : (
          <LockedField
            label={f('phone')}
            value={dashIfBlank(maskPhone(patient.phone))}
            reason={lock('appAccountPhone')}
          />
        )}
        {/* SHCT-915 — PRD 02 v1.23 §3.6. 거주지와 상세 주소는 **각각 한 행**이고 둘 다 수정 대상이다.
            종전 SHCT-737 QC ① 은 두 값이 환자 앱 계정 쪽 행에 있어 어드민 수정이 「패널이 보여주지도
            않는 두 번째 사본」을 만들던 시절의 결론이었다. 주소가 환자 본인 임상 행으로 옮겨오면서
            (admin-api V141) 전제가 사라졌고, §4.1 의 「어드민은 조회·표시만 한다」 문장도 삭제됐다.
            이어지는 「어드민 CRUD 화면은 제공하지 않는다」는 도로명주소 **데이터셋**(행안부 API 소관)
            관리 화면을 뜻하므로 환자 개별 값의 편집과 무관하다.
            편집기를 현재 값으로 채운다 — 이름·휴대폰과 달리 주소는 서버가 마스킹하지 않으므로 빈 칸에서
            시작할 이유가 없고, 주소 정정은 대개 한두 글자를 고치는 일이다. */}
        <DetailField
          label={f('residence')}
          value={editable(
            'residence',
            residenceText,
            <TextEditor
              initial={patient.residence ?? ''}
              maxLength={RESIDENCE_MAX}
              serverError={errorFor('residence')}
              saving={saving}
              buildInput={(residence) => ({ residence })}
              onSave={(input) => onSave(input, 'residence')}
              onCancel={onCancel}
            />,
          )}
        />
        <DetailField
          label={f('residenceDetail')}
          value={editable(
            'residenceDetail',
            residenceDetailText,
            <TextEditor
              initial={patient.residenceDetail ?? ''}
              maxLength={RESIDENCE_DETAIL_MAX}
              serverError={errorFor('residenceDetail')}
              saving={saving}
              buildInput={(residenceDetail) => ({ residenceDetail })}
              onSave={(input) => onSave(input, 'residenceDetail')}
              onCancel={onCancel}
            />,
          )}
        />
      </DetailSection>

      {/* SHCT-738 — PRD 02 v1.20 §3.6 「2. 진료 정보」, in the order the section table lists:
          주진단 · 부진단 · 처방약 · 치료기관 · 등록번호(MRN) · 퇴원일.
          [ADM.064 ①] settled that there are TWO diagnosis rows, not three. The row that used to read
          `질병코드` was always the 부진단 — admin-api builds it from `sub_diagnosis_code_id` — and it now
          says so, with 주진단 above it. Both rows open the SAME editor: the server validates the pair. */}
      <DetailSection title={s('clinical')}>
        <DetailField
          label={f('mainDiagnosis')}
          value={editable(
            'diagnosis',
            mainDiagnosisText,
            <DiagnosisEditor
              patient={patient}
              saving={saving}
              onSave={(input) => onSave(input, 'diagnosis')}
              onCancel={onCancel}
            />,
          )}
        />
        <DetailField
          label={f('subDiagnosis')}
          value={
            editing === 'diagnosis' ? (
              // The pair is being corrected one row above; showing a second editor here would let the
              // operator start two drafts of the same value.
              <span style={{ color: 'var(--muted-foreground)' }}>
                {t('patientManagement.panel.edit.editingAbove')}
              </span>
            ) : (
              <EditableValue
                value={diseaseText}
                canWrite={canWrite}
                denyTitle={denyTitle}
                onStart={() => onStart('diagnosis')}
                editLabel={t('patientManagement.panel.edit.startAria', {
                  field: f('subDiagnosis'),
                })}
                editHint={t('patientManagement.panel.edit.hint')}
              />
            )
          }
        />
        <DetailField
          label={f('prescription')}
          span
          value={editable(
            'medication',
            medications.length === 0 ? (
              '-'
            ) : (
              <ul className="detail-rx-list">
                {medications.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            ),
            <MedicationEditor
              patient={patient}
              saving={saving}
              onSave={(input) => onSave(input, 'medication')}
              onCancel={onCancel}
            />,
          )}
        />
        <LockedField
          label={f('institution')}
          value={institutionText}
          reason={lock('institution')}
        />
        {/* PRD 02 v1.22 §3.6 「편집 가능 필드」 — 등록번호(MRN)는 `❌ 환자 고유 식별키 — 중복 판정의
            기준이라 바꾸지 않는다`. SHCT-737 의 필드표와 수용 기준은 MRN 을 수정 대상으로 적고 있으나,
            요구사항 우선순위상 기능 PRD 가 티켓 본문을 이긴다. 서버는 아직 `mrn` 을 받으므로 이 잠금은
            화면 쪽 규정이며, BE 도 함께 막는 일은 핸드오버로 넘긴다. */}
        <LockedField label={f('regNo')} value={dashIfBlank(patient.regNo)} reason={lock('mrn')} />
        <DetailField
          label={f('dischargeDate')}
          value={editable(
            'dischargeDate',
            formatDate(patient.dischargeDate),
            <DateEditor
              initial={toDateInputValue(patient.dischargeDate)}
              max={todayInKst()}
              requireBeforeToday
              saving={saving}
              buildInput={(dischargeDate) => ({ dischargeDate })}
              onSave={(input) => onSave(input, 'dischargeDate')}
              onCancel={onCancel}
            />,
          )}
        />
      </DetailSection>

      {/* SHCT-794 — PRD 02 v1.16 §3.6 「3. 앱·계정」, resolved by [ADM.063].
          앱 사용자 유형 and the guardian block are rendered ONLY when the wire supplies them. */}
      <DetailSection title={s('appAccount')}>
        {appUserTypeText && (
          <LockedField
            label={f('appUserType')}
            value={appUserTypeText}
            reason={lock('appUserType')}
          />
        )}
        {/* [ADM.063 ②] — 본인 모드에서는 보호자 항목을 숨긴다. `-` 를 쓰지 않는 이유는 그것이
            "보호자가 있으나 데이터가 누락됐다"는 다른 뜻이 되기 때문이다. §3.7 등록 모달과 같은 처리. */}
        {guardianMode && (
          <>
            {/* 보호자 이름도 환자 이름과 같다 — 마스킹된 값이라 채우지 않고 빈 칸에서 시작한다.
                길이 규칙은 §8 부록 NAME 클래스(1~50자)이며 한글 고정이 아니다. */}
            <DetailField
              label={f('guardianName')}
              value={editable(
                'guardianName',
                dashIfBlank(maskName(patient.guardianName ?? null)),
                <TextEditor
                  initial=""
                  maxLength={GUARDIAN_NAME_MAX}
                  placeholder={t('patientManagement.panel.edit.phiPlaceholder')}
                  validate={(v) =>
                    v.length >= GUARDIAN_NAME_MIN
                      ? null
                      : t('patientManagement.panel.edit.errors.required')
                  }
                  serverError={errorFor('guardianName')}
                  saving={saving}
                  buildInput={(guardianName) => ({ guardianName })}
                  onSave={(input) => onSave(input, 'guardianName')}
                  onCancel={onCancel}
                />,
              )}
            />
            <DetailField
              label={f('guardianRelationship')}
              value={editable(
                'guardianRelationship',
                patient.guardianRelationship
                  ? t(
                      `patientManagement.guardianRelationships.${patient.guardianRelationship}`,
                    )
                  : '-',
                <SelectEditor<PatientGuardianRelationship>
                  initial={patient.guardianRelationship ?? ''}
                  options={GUARDIAN_RELATIONSHIPS}
                  optionLabel={(r) => t(`patientManagement.guardianRelationships.${r}`)}
                  placeholder={t(
                    'patientManagement.registrationModal.guardian.relationshipPlaceholder',
                  )}
                  saving={saving}
                  buildInput={(guardianRelationship) => ({ guardianRelationship })}
                  onSave={(input) => onSave(input, 'guardianRelationship')}
                  onCancel={onCancel}
                />,
              )}
            />
            {/* [ADM.063 ⑤] — 보호자 번호도 환자와 동일 PHI라 같은 마스킹 규칙을 쓴다. */}
            <LockedField
              label={f('guardianPhone')}
              value={patient.guardianPhone ? maskPhone(patient.guardianPhone) : '-'}
              reason={lock('guardianAccountPhone')}
            />
          </>
        )}
        <LockedField
          label={f('registeredAt')}
          value={formatDate(patient.registeredAt)}
          reason={lock('systemGenerated')}
        />
        {/* [ADM.063 ③] — v1.16 의 「계정 상태」는 앱 계정 상태로 오독되던 이름이라 「관리 상태」로 정정됐다.
            값·출처는 §4.4 canonical(관리중/관리종료/탈퇴)이고 전환은 §3.8 별도 액션이 담당하므로 읽기 전용.
            상단 상태 칩과 같은 값을 다시 보여주는 것은 의도된 중복이다. */}
        <LockedField
          label={f('careStatus')}
          value={t(`patientManagement.statusBadges.${patient.status}`)}
          reason={lock('careStatus')}
        />
      </DetailSection>

      {/* [ADM.063 ④] — 「4. 이력」은 마지막 PRO 만 남는다. 회원가입일은 「3. 앱·계정」으로 옮겼고,
          관리 상태 변경 이력은 별도 백로그 CR(SHCT-886)로 분리됐다. */}
      <DetailSection title={s('history')}>
        <LockedField label={f('lastPro')} value={lastProText} reason={lock('systemGenerated')} />
      </DetailSection>
    </>
  );
}

/**
 * The chip row above the grid: 상태 칩 · 보호자 배지 · 종료일.
 *
 * <p>The 보호자 badge sits HERE, beside the status chip, because that is where the admin prototype
 * (`pages/patients.html`) puts it. PRD 02 v1.20 §3.6's 패널 표시 필드 table instead writes the same
 * badge into the 「이름」 row, so the two documents disagree on placement alone — the badge, its
 * condition and its meaning are identical in both. Placement follows the prototype and the difference
 * is raised rather than settled here. The LIST badge is not affected: §4.1 names the 이름 column for
 * it explicitly, and the prototype simply has not caught up there yet.
 *
 * <p>Guardian mode is `appUserType === 'guardian'` and nothing else — an absent field means the wire
 * has not said, which draws no badge rather than asserting 본인.
 */
export function PanelHeader({ patient }: { patient: Patient }) {
  const { t } = useTranslation('admin');
  const guardianMode = patient.appUserType === 'guardian';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 'var(--space-3)',
        flexWrap: 'wrap',
      }}
    >
      <PatientStatusBadge patient={patient} />
      {guardianMode && (
        <span className="badge badge-primary" style={{ fontSize: 10, padding: '2px 6px' }}>
          {t('patientManagement.badges.guardian')}
        </span>
      )}
      {patient.status === 'CLOSED' && patient.closedAt && (
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
          {t('patientManagement.panel.closedAt', { date: formatDate(patient.closedAt) })}
        </span>
      )}
    </div>
  );
}

function PanelBody({
  patient,
  editing,
  setEditing,
}: {
  patient: Patient;
  editing: EditKey | null;
  setEditing: (key: EditKey | null) => void;
}) {
  const { t } = useTranslation('admin');
  const { canWrite, tooltip } = useWriteGuard('PATIENT');
  const updatePatient = useUpdatePatient(patient.id);
  // §3.6 조건 분기 — 퇴원일 변경은 저장 전에 PRO 재계산을 안내한다. 퇴원일은 `등록일자 = 치료시작일 =
  // PRO 기준일` 단일 필드라 값이 바뀌면 스케줄 전체가 다시 산정된다(의료진웹 03 §3.3과 동일 취급).
  const [pendingDischarge, setPendingDischarge] = useState<UpdatePatientInput | null>(null);
  const [serverError, setServerError] = useState<{ key: EditKey; message: string } | null>(null);

  // SHCT-737 — a rejection that names ONE field is answered on that field, not by the generic toast.
  // 환자 휴대폰 made this necessary: the number is a lookup key, so the server refuses one another patient
  // already holds, and "저장에 실패했습니다" tells an operator nothing they can act on. The wording is
  // mapped from the CODE, not taken from the server's text, so it stays KO/EN — and it reuses the copy the
  // registration modal already had customer-confirmed for the same collision (Confluence Q70).
  const FIELD_ERRORS: Partial<Record<PatientApiErrorCode, { key: EditKey; message: string }>> = {
    PATIENT_MOBILE_DUPLICATE: {
      key: 'phone',
      message: t('patientManagement.registrationModal.errors.phoneDuplicate'),
    },
    PHONE_INVALID: {
      key: 'phone',
      message: t('patientManagement.panel.edit.errors.phone'),
    },
    // SHCT-915 — the editor already checks both lengths, so these only arrive when a paste slipped past
    // it. Answering on the field keeps the typed value on screen; the generic toast would discard it.
    RESIDENCE_TOO_LONG: {
      key: 'residence',
      message: t('patientManagement.panel.edit.errors.tooLong', { max: RESIDENCE_MAX }),
    },
    RESIDENCE_DETAIL_TOO_LONG: {
      key: 'residenceDetail',
      message: t('patientManagement.panel.edit.errors.tooLong', { max: RESIDENCE_DETAIL_MAX }),
    },
  };

  const commit = useCallback(
    (input: UpdatePatientInput) => {
      setServerError(null);
      updatePatient.mutate(input, {
        onSuccess: () => {
          setEditing(null);
          setPendingDischarge(null);
          toast.success(t('patientManagement.toasts.updateSuccess'));
        },
        onError: (e) => {
          const field = e instanceof PatientApiError ? FIELD_ERRORS[e.code] : undefined;
          if (field) {
            // The editor stays open, holding what was typed, with the reason under it.
            setServerError(field);
            return;
          }
          // The server's own message is not surfaced here on purpose: a generic INTERNAL_ERROR body
          // would replace the PRD's wording with something an operator cannot act on.
          toast.error(t('patientManagement.toasts.updateError'));
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatePatient, setEditing, t],
  );

  const handleSave = useCallback(
    (input: UpdatePatientInput, key: EditKey) => {
      if (key === 'dischargeDate') {
        setPendingDischarge(input);
        return;
      }
      commit(input);
    },
    [commit],
  );

  return (
    <>
      <PanelHeader patient={patient} />

      <PatientFieldGrid
        patient={patient}
        canWrite={canWrite}
        denyTitle={tooltip}
        editing={editing}
        // Opening or abandoning an editor drops the last rejection: it described a value that is no
        // longer on screen, and a message outliving the attempt it belongs to reads as a live error.
        onStart={(key) => {
          setServerError(null);
          setEditing(key);
        }}
        onCancel={() => {
          setServerError(null);
          setEditing(null);
        }}
        onSave={handleSave}
        saving={updatePatient.isPending}
        serverError={serverError}
      />

      <ConfirmModal
        open={pendingDischarge != null}
        title={t('patientManagement.panel.dischargeRecalc.title')}
        message={t('patientManagement.panel.dischargeRecalc.message')}
        confirmLabel={t('common:actions.confirm')}
        cancelLabel={t('patientManagement.actions.cancel')}
        loading={updatePatient.isPending}
        loadingLabel={t('patientManagement.actions.saving')}
        onConfirm={() => pendingDischarge && commit(pendingDischarge)}
        onCancel={() => {
          setPendingDischarge(null);
          setEditing(null);
        }}
      />
    </>
  );
}

function PanelContent({
  id,
  editing,
  setEditing,
}: {
  id: number;
  editing: EditKey | null;
  setEditing: (key: EditKey | null) => void;
}) {
  const { t } = useTranslation('admin');
  const { data, isLoading, error, refetch } = usePatientQuery(id);

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
        {t('patientManagement.panel.loading')}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        role="alert"
        style={{
          padding: 16,
          border: '1px solid var(--destructive)',
          borderRadius: 8,
          background: '#FEE2E8',
          color: 'var(--destructive)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span>{t('patientManagement.panel.loadError')}</span>
        <button type="button" className="btn btn-sm btn-outline" onClick={() => void refetch()}>
          {t('patientManagement.actions.retry')}
        </button>
      </div>
    );
  }
  return <PanelBody patient={data} editing={editing} setEditing={setEditing} />;
}

export function PatientDetailPanel() {
  const { t } = useTranslation('admin');
  const openId = usePatientDetailStore((s) => s.openId);
  const closePanel = usePatientDetailStore((s) => s.close);
  const [editing, setEditing] = useState<EditKey | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  // Clicking another row swaps `openId` without closing the panel, so an editor left open would carry a
  // draft onto a different patient's field. Reset on every patient change rather than on close alone.
  useEffect(() => {
    setEditing(null);
    setConfirmClose(false);
  }, [openId]);

  // §3.6 시나리오 4 / 조건 분기 — 수정 중인 필드가 있으면 확인 후 닫는다. Without this the operator
  // loses a half-typed correction to a stray click on the overlay with no way to know it happened.
  const requestClose = useCallback(() => {
    if (editing != null) {
      setConfirmClose(true);
      return;
    }
    closePanel();
  }, [editing, closePanel]);

  const discardAndClose = useCallback(() => {
    setConfirmClose(false);
    setEditing(null);
    closePanel();
  }, [closePanel]);

  return (
    <>
      <SlidePanel
        open={openId != null}
        title={t('patientManagement.panel.title')}
        width={PANEL_WIDTH}
        onClose={requestClose}
      >
        {openId != null && (
          <PanelContent id={openId} editing={editing} setEditing={setEditing} />
        )}
      </SlidePanel>

      <ConfirmModal
        open={confirmClose}
        title={t('patientManagement.panel.discardOnClose.title')}
        message={t('patientManagement.panel.discardOnClose.message')}
        confirmLabel={t('patientManagement.panel.discardOnClose.confirm')}
        cancelLabel={t('patientManagement.panel.discardOnClose.cancel')}
        onConfirm={discardAndClose}
        onCancel={() => setConfirmClose(false)}
      />
    </>
  );
}
